import React from 'react';
import PropTypes from 'prop-types';
import { Box, Card, CardContent, CardHeader, Chip, IconButton, List, ListItem, ListItemSecondaryAction, Tooltip, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import withObservables from '@nozbe/with-observables'
import { Q } from '@nozbe/watermelondb'
import _ from 'lodash'
import dayjs from 'dayjs';
import FuotaButton from './fuota-button';
import RevokeButton from './revoke-button';
import { withRegistrationDatabase } from '../../../model/registration/RegistrationDatabase';
import { useTranslation } from 'react-i18next';
import { useDownloadFirmwareFile } from '../../../hooks/use-download';

const FirmwareField = ({ label, value, width }) => (
    <Box sx={{ width, minWidth: width, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2" noWrap>{value || '-'}</Typography>
    </Box>
);

FirmwareField.propTypes = {
    label: PropTypes.string,
    value: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export function FirmwareListItem({ firmware, device, index }) {

    const downloadFirmwareFile = useDownloadFirmwareFile();

    const handleDownloadFactory = async (e) => {
        e.stopPropagation();
        await downloadFirmwareFile(firmware.id, 'factory', `factory-${firmware.version}.zip`);
    };

    const handleDownloadOta = async (e) => {
        e.stopPropagation();
        await downloadFirmwareFile(firmware.id, 'ota', `ota-${firmware.version}.zip`);
    };

    return (
        <ListItem>
            <Box sx={{ display: 'flex', gap: 2, flex: 1, alignItems: 'flex-start' }}>
                <FirmwareField label="#" value={String(index + 1)} width={50} />
                <FirmwareField label="Version" value={firmware.version} width={300} />
                <FirmwareField label="ID" value={firmware.id} width={200} />
                <FirmwareField label="Created" value={dayjs(firmware.createdAt).format('YYYY-MM-DD')} width={90} />
                {(firmware.factoryFile || firmware.otaFile) && (
                    <Box sx={{ flexShrink: 0 }}>
                        <Typography variant="caption" color="text.secondary">Download</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {firmware.factoryFile && (
                                <Tooltip title="Factory">
                                    <IconButton size="small" onClick={handleDownloadFactory}>
                                        <DownloadIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {firmware.otaFile && (
                                <Tooltip title="OTA/DFU">
                                    <IconButton size="small" onClick={handleDownloadOta}>
                                        <DownloadIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
            <ListItemSecondaryAction>
                {device && (device.firmwareId !== firmware.id) && (
                    <FuotaButton firmware={firmware} device={device} />
                )}
                {!device && !firmware.revoke && (
                    <RevokeButton firmware={firmware} />
                )}
                {firmware.revoke && <Chip label='REVOKED' size="small" sx={{ alignSelf: 'center' }} />}
            </ListItemSecondaryAction>
        </ListItem>
    )
}

FirmwareListItem.propTypes = {
    firmware: PropTypes.any,
    device: PropTypes.any,
    index: PropTypes.number
}

const observeFirmware = withObservables(['firmware'], ({ firmware }) => ({
    firmware,
}));

export const ObservedFirmwareListItem = observeFirmware(FirmwareListItem);

export function FirmwaresListInternal({ firmwares, ...props }) {

    if (firmwares.length === 0) {
        return <Typography align='center'>No Firmwares</Typography>
    }

    return (
        <List>
            {_.map(firmwares, (firmware, index) => <ObservedFirmwareListItem key={firmware.id} firmware={firmware} index={index} {...props} />)}
        </List>
    );
}

FirmwaresListInternal.propTypes = {
    firmwares: PropTypes.array
}

const observeFirmwares = withObservables([], ({ database, hardware = undefined, filterRevoked = false }) => {

    const queries = [];
    if (hardware) {
        queries.push(Q.where('type', hardware));
    }

    if(filterRevoked) {
        queries.push(Q.where('revoke', false));
    }

    queries.push(Q.sortBy('created_at', Q.desc));

    return {
        firmwares: database.get('firmwares').query(...queries)
    };
});

export const ObservedFirmwaresList = withRegistrationDatabase(observeFirmwares(FirmwaresListInternal))

export default function FirmwaresList({ title = "FIRMWARES.LIST_HEADER", action = null, ...props }) {
    const { t } = useTranslation();

    return (
        <Card>
            <CardHeader
                action={action}
                title={t(title)}
            />
            <CardContent>
                <ObservedFirmwaresList {...props} />
            </CardContent>
        </Card>
    );
};

FirmwaresList.propTypes = {
    title: PropTypes.string,
    action: PropTypes.node
}
